import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDateTime,
  formatMoney,
  moneyInputError,
  moneyToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import {
  creditAuthority,
  decisionLabels,
  impedimentLabels,
  impedimentOrder,
  impedimentSources,
  policySituationLabels,
  releasableImpediments,
  situationLabels,
  type AuthorityLevelValue,
  type AuthorityMatrixValues,
  type DecisionType,
  type EngineFlowValues,
  type ImpedimentPolicyValues,
  type PolicyKind,
  type PolicyVersion,
  type ReviewPolicyValues,
  type ReviewSituation,
} from './api';

/**
 * Configuracao da empresa: pareceres, alcadas, bloqueios de emissao e motor no fluxo
 * (C-ALC).
 *
 * Cada assunto tem versao: rascunho, edicao, ativacao por outra pessoa com
 * justificativa e data de vigencia. Sem versao ativada vale o padrao, que e o
 * comportamento de antes. Nenhum valor de alcada vem preenchido: nenhuma alcada esta
 * documentada, e quem informa e a seguradora.
 *
 * Fica em Configuracoes > Analise de credito, na secao "Pareceres, alcadas e
 * bloqueios" (`creditSettings/EntradaDaAnaliseDeCredito.tsx`), para quem le a matriz de
 * alcadas ou a politica de credito, com a funcionalidade `credito.analise`.
 */
export function CreditAuthoritySettings() {
  return (
    <div className="flex flex-col gap-5">
      <PolicyPanel
        kind="pareceres"
        title="Pareceres"
        description="O que cada parecer exige alem do que o sistema ja exige. A reprovacao sempre exige motivo."
        render={(values, onChange) => (
          <ReviewPolicyEditor
            values={values as ReviewPolicyValues}
            onChange={onChange}
          />
        )}
      />
      <PolicyPanel
        kind="alcadas"
        title="Alcadas"
        description="Niveis por perfil ou permissao, com teto por tipo de decisao. Vazia, vale a permissao de aprovar, como antes."
        render={(values, onChange) => (
          <AuthorityMatrixEditor
            values={values as AuthorityMatrixValues}
            onChange={onChange}
          />
        )}
      />
      <PolicyPanel
        kind="bloqueios"
        title="Bloqueios de emissao"
        description="Impedimentos de credito que bloqueiam a emissao. Todos nascem desligados."
        render={(values, onChange) => (
          <ImpedimentPolicyEditor
            values={values as ImpedimentPolicyValues}
            onChange={onChange}
          />
        )}
      />
      <PolicyPanel
        kind="fluxo"
        title="Motor no fluxo da proposta"
        description="Execucao do motor de credito no envio para analise. Desligada por padrao."
        render={(values, onChange) => (
          <EngineFlowEditor values={values as EngineFlowValues} onChange={onChange} />
        )}
      />
    </div>
  );
}

function permissionsOf(kind: PolicyKind) {
  const prefixo = kind === 'alcadas' ? 'alcadas.matriz' : 'credito.politica';
  return {
    view: `${prefixo}.view`,
    update: `${prefixo}.update`,
    approve: `${prefixo}.approve`,
  };
}

function PolicyPanel({
  kind,
  title,
  description,
  render,
}: {
  kind: PolicyKind;
  title: string;
  description: string;
  render: (values: unknown, onChange: (values: unknown) => void) => ReactNode;
}) {
  const { can } = useAuth();
  const perm = permissionsOf(kind);
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeVer = can(perm.view);

  const atual = useQuery({
    queryKey: ['credit-authority-policy', kind],
    queryFn: ({ signal }) => creditAuthority.current(kind, signal),
    enabled: podeVer,
  });
  const versoes = useQuery({
    queryKey: ['credit-authority-versions', kind],
    queryFn: ({ signal }) => creditAuthority.versions(kind, signal),
    enabled: podeVer,
  });

  function recarregar() {
    void queryClient.invalidateQueries({ queryKey: ['credit-authority-policy', kind] });
    void queryClient.invalidateQueries({
      queryKey: ['credit-authority-versions', kind],
    });
  }

  const abrir = useMutation({
    mutationFn: () => creditAuthority.createDraft(kind),
    onSuccess: () => {
      toast.success('Rascunho aberto.');
      recarregar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!podeVer) return null;

  if (atual.isPending || versoes.isPending) {
    return (
      <Card>
        <CardHeader title={title} description={description} />
        <CardBody>
          <SkeletonText lines={3} />
        </CardBody>
      </Card>
    );
  }
  if (atual.isError || versoes.isError) {
    return (
      <Alert tone="danger" title={`Nao foi possivel carregar: ${title}`}>
        {(atual.error ?? versoes.error)?.message}
      </Alert>
    );
  }

  const rascunho = versoes.data.find((v) => v.status === 'rascunho');
  return (
    <Card>
      <CardHeader
        title={title}
        description={description}
        action={
          atual.data.is_default ? (
            <Badge tone="neutral">Padrao (comportamento de antes)</Badge>
          ) : (
            <Badge tone="success">Vigente: versao {atual.data.version}</Badge>
          )
        }
      />
      <CardBody className="flex flex-col gap-4">
        {atual.data.scheduled ? (
          <Alert tone="info" title="Versao agendada">
            A versao {atual.data.scheduled.version} passa a valer em{' '}
            {formatDateTime(atual.data.scheduled.effective_from)}.
          </Alert>
        ) : null}

        {rascunho ? (
          <DraftEditor
            key={rascunho.id + rascunho.values_hash}
            kind={kind}
            draft={rascunho}
            canEdit={can(perm.update)}
            canApprove={can(perm.approve)}
            render={render}
            onChanged={recarregar}
          />
        ) : can(perm.update) ? (
          <div>
            <Button
              variant="secondary"
              loading={abrir.isPending}
              onClick={() => abrir.mutate()}
            >
              Abrir rascunho
            </Button>
          </div>
        ) : (
          <fieldset disabled className="opacity-80">
            {render(atual.data.values, () => undefined)}
          </fieldset>
        )}

        <VersionsList versions={versoes.data} />
      </CardBody>
    </Card>
  );
}

function DraftEditor({
  kind,
  draft,
  canEdit,
  canApprove,
  render,
  onChanged,
}: {
  kind: PolicyKind;
  draft: PolicyVersion;
  canEdit: boolean;
  canApprove: boolean;
  render: (values: unknown, onChange: (values: unknown) => void) => ReactNode;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [valores, setValores] = useState<unknown>(draft.values);
  const [justificativa, setJustificativa] = useState('');
  const [vigencia, setVigencia] = useState('');
  const naoSalvo = JSON.stringify(valores) !== JSON.stringify(draft.values);

  const salvar = useMutation({
    mutationFn: () => creditAuthority.updateDraft(kind, draft.id, valores),
    onSuccess: () => {
      toast.success('Rascunho salvo.');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const ativar = useMutation({
    // A data de vigencia e o dia no fuso de Brasilia: meia-noite de Brasilia e 03h UTC.
    mutationFn: () =>
      creditAuthority.activate(
        kind,
        draft.id,
        justificativa,
        vigencia ? `${vigencia}T03:00:00Z` : undefined,
      ),
    onSuccess: (versao) => {
      toast.success(
        versao.situation === 'agendada' ? 'Versao agendada.' : 'Versao ativada.',
      );
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-4 rounded-md border border-line p-4">
      <p className="text-sm font-medium text-ink-900">
        Rascunho da versao {draft.version}
        {draft.based_on_version ? `, a partir da versao ${draft.based_on_version}` : ''}
      </p>
      <fieldset disabled={!canEdit || salvar.isPending}>
        {render(valores, setValores)}
      </fieldset>
      {canEdit ? (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            loading={salvar.isPending}
            disabled={!naoSalvo}
            onClick={() => salvar.mutate()}
          >
            Salvar rascunho
          </Button>
        </div>
      ) : null}
      {canApprove ? (
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <p className="text-2xs text-ink-500">
            Quem criou ou editou o rascunho nao o ativa: a ativacao e de outra pessoa.
          </p>
          <Textarea
            label="Justificativa da ativacao"
            value={justificativa}
            onChange={(event) => setJustificativa(event.target.value)}
          />
          <Field
            label="Vale a partir de"
            type="date"
            value={vigencia}
            hint="Vazio vale agora. Data futura deixa a versao agendada; a vigencia nunca e retroativa."
            onChange={(event) => setVigencia(event.target.value)}
          />
          <div className="flex justify-end">
            <Button
              loading={ativar.isPending}
              disabled={justificativa.trim() === '' || naoSalvo}
              onClick={() => ativar.mutate()}
            >
              Ativar versao
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function VersionsList({ versions }: { versions: PolicyVersion[] }) {
  const ativadas = versions.filter((v) => v.status === 'ativa');
  if (ativadas.length === 0) {
    return (
      <p className="text-2xs text-ink-500">
        Nenhuma versao ativada: vale o padrao, que reproduz o comportamento de antes.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1 text-2xs text-ink-600">
      {ativadas.map((v) => (
        <li key={v.id}>
          Versao {v.version}: {policySituationLabels[v.situation]}, ativada por{' '}
          {v.activated_label} em {formatDateTime(v.activated_at)}, vale desde{' '}
          {formatDateTime(v.effective_from)}. {v.justification}
        </li>
      ))}
    </ul>
  );
}

// --- Pareceres -----------------------------------------------------------------------

const revisoes: { key: 'credito' | 'subscricao' | 'juridico'; label: string }[] = [
  { key: 'credito', label: 'Credito' },
  { key: 'subscricao', label: 'Subscricao' },
  { key: 'juridico', label: 'Juridico' },
];

const todasSituacoes: ReviewSituation[] = [
  'motor_analise_manual',
  'motor_recusa',
  'motor_pendente',
  'motor_desvio_para_esteira',
  'motor_nao_executado',
];

export function ReviewPolicyEditor({
  values,
  onChange,
}: {
  values: ReviewPolicyValues;
  onChange: (values: ReviewPolicyValues) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      {revisoes.map(({ key, label }) => {
        const req = values.pareceres[key];
        const juridico = key === 'juridico';
        function muda(parcial: Partial<typeof req>) {
          onChange({
            pareceres: { ...values.pareceres, [key]: { ...req, ...parcial } },
          });
        }
        return (
          <div
            key={key}
            className="flex flex-col gap-2 rounded-md border border-line p-3"
          >
            <p className="text-sm font-medium text-ink-900">{label}</p>
            <Select
              label={`Parecer escrito na aprovacao (${label})`}
              value={req.parecer_escrito}
              disabled={juridico}
              hint={
                juridico
                  ? 'O parecer juridico escrito e sempre obrigatorio.'
                  : undefined
              }
              options={[
                { value: 'facultativo', label: 'Facultativo' },
                { value: 'obrigatorio', label: 'Obrigatorio' },
                {
                  value: 'nas_situacoes',
                  label: 'Obrigatorio nas situacoes do motor marcadas',
                },
              ]}
              onChange={(event) =>
                muda({
                  parecer_escrito: event.target.value as typeof req.parecer_escrito,
                  situacoes:
                    event.target.value === 'nas_situacoes' ? req.situacoes : [],
                })
              }
            />
            {req.parecer_escrito === 'nas_situacoes' ? (
              <div className="flex flex-col gap-1">
                {todasSituacoes.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 text-sm text-ink-700"
                  >
                    <input
                      type="checkbox"
                      checked={req.situacoes.includes(s)}
                      onChange={(event) =>
                        muda({
                          situacoes: event.target.checked
                            ? [...req.situacoes, s]
                            : req.situacoes.filter((x) => x !== s),
                        })
                      }
                    />
                    {situationLabels[s]}
                  </label>
                ))}
              </div>
            ) : null}
            {!juridico ? (
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={req.justificativa_contra_motor}
                  onChange={(event) =>
                    muda({ justificativa_contra_motor: event.target.checked })
                  }
                />
                Exigir justificativa na aprovacao contra a recomendacao do motor (
                {label})
              </label>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// --- Alcadas -------------------------------------------------------------------------

const tiposDeDecisao: DecisionType[] = [
  'limite_credito',
  'subscricao_is',
  'liberacao_bloqueio',
];

export function AuthorityMatrixEditor({
  values,
  onChange,
}: {
  values: AuthorityMatrixValues;
  onChange: (values: AuthorityMatrixValues) => void;
}) {
  const decisoes = values.decisoes ?? {};
  const pep = values.pep_nivel_minimo ?? {};
  return (
    <div className="flex flex-col gap-4">
      <Alert tone="info" title="Nenhuma alcada esta documentada">
        Os niveis e tetos sao da seguradora. Sem niveis para um tipo de decisao, vale a
        permissao de aprovar.
      </Alert>
      {tiposDeDecisao.map((tipo) => {
        const niveis = decisoes[tipo] ?? [];
        function grava(novos: AuthorityLevelValue[], pepNivel?: number | null) {
          const proximoPep = { ...pep };
          const nivelPep = pepNivel === undefined ? pep[tipo] : pepNivel;
          if (nivelPep && novos.some((n) => n.nivel === nivelPep))
            proximoPep[tipo] = nivelPep;
          else delete proximoPep[tipo];
          const proximas = { ...decisoes };
          if (novos.length > 0) proximas[tipo] = novos;
          else delete proximas[tipo];
          onChange({ decisoes: proximas, pep_nivel_minimo: proximoPep });
        }
        return (
          <div
            key={tipo}
            className="flex flex-col gap-3 rounded-md border border-line p-3"
          >
            <p className="text-sm font-medium text-ink-900">{decisionLabels[tipo]}</p>
            {niveis.map((nivel, i) => (
              <LevelRow
                key={`${tipo}-${nivel.nivel}`}
                tipo={tipo}
                level={nivel}
                onChange={(atualizado) =>
                  grava(niveis.map((n, j) => (j === i ? atualizado : n)))
                }
                onRemove={() =>
                  grava(
                    niveis
                      .filter((_, j) => j !== i)
                      .map((n, j) => ({ ...n, nivel: j + 1 })),
                  )
                }
              />
            ))}
            <div className="flex flex-wrap items-end gap-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  grava([
                    ...niveis,
                    {
                      nivel: niveis.length + 1,
                      nome: '',
                      tipo_titular: 'perfil',
                      titular: '',
                      teto: null,
                    },
                  ])
                }
              >
                Acrescentar nivel
              </Button>
              {niveis.length > 0 ? (
                <Select
                  label={`Nivel minimo para operacao com PEP (${decisionLabels[tipo]})`}
                  value={pep[tipo] ? String(pep[tipo]) : ''}
                  hint="Res. SUSEP 99/2026, art. 33, a conferir em fonte oficial. Vazio: nao se aplica."
                  options={[
                    { value: '', label: 'Nao se aplica' },
                    ...niveis.map((n) => ({
                      value: String(n.nivel),
                      label: `Nivel ${n.nivel}`,
                    })),
                  ]}
                  onChange={(event) =>
                    grava(
                      niveis,
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LevelRow({
  tipo,
  level,
  onChange,
  onRemove,
}: {
  tipo: DecisionType;
  level: AuthorityLevelValue;
  onChange: (level: AuthorityLevelValue) => void;
  onRemove: () => void;
}) {
  // Teto zero e um teto (nada decide), e nao "sem teto": o campo mostra 0.
  const [teto, setTeto] = useState(
    level.teto ? (level.teto.amount === 0 ? '0' : moneyToInput(level.teto)) : '',
  );
  const erro = teto.trim() === '' ? undefined : moneyInputError(teto);
  const sufixo = `${decisionLabels[tipo]}, nivel ${level.nivel}`;
  return (
    <div className="grid gap-3 sm:grid-cols-5">
      <Field
        label={`Nome (${sufixo})`}
        value={level.nome}
        onChange={(event) => onChange({ ...level, nome: event.target.value })}
      />
      <Select
        label={`Ocupado por (${sufixo})`}
        value={level.tipo_titular}
        options={[
          { value: 'perfil', label: 'Perfil' },
          { value: 'permissao', label: 'Permissao' },
        ]}
        onChange={(event) =>
          onChange({
            ...level,
            tipo_titular: event.target.value as 'perfil' | 'permissao',
          })
        }
      />
      <Field
        label={`Chave do perfil ou permissao (${sufixo})`}
        value={level.titular}
        onChange={(event) => onChange({ ...level, titular: event.target.value.trim() })}
      />
      <MoneyField
        label={`Teto em R$ (${sufixo})`}
        value={teto}
        error={erro}
        hint={level.teto ? `Gravado: ${formatMoney(level.teto)}` : 'Vazio: sem teto.'}
        onValueChange={(texto) => {
          setTeto(texto);
          if (texto.trim() === '') onChange({ ...level, teto: null });
          else if (moneyInputError(texto) === undefined)
            onChange({ ...level, teto: toMoney(texto) });
        }}
      />
      <div className="flex items-end">
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remover nivel
        </Button>
      </div>
    </div>
  );
}

// --- Bloqueios -----------------------------------------------------------------------

export function ImpedimentPolicyEditor({
  values,
  onChange,
}: {
  values: ImpedimentPolicyValues;
  onChange: (values: ImpedimentPolicyValues) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
        {impedimentOrder.map((code) => {
          const atual = values.impedimentos[code] ?? {
            ligado: false,
            liberavel: false,
          };
          const liberavelPermitido = releasableImpediments.includes(code);
          function muda(parcial: Partial<typeof atual>) {
            onChange({
              ...values,
              impedimentos: {
                ...values.impedimentos,
                [code]: { ...atual, ...parcial },
              },
            });
          }
          return (
            <li key={code} className="flex flex-col gap-1 px-3 py-2">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm font-medium text-ink-900">
                  <input
                    type="checkbox"
                    checked={atual.ligado}
                    onChange={(event) => muda({ ligado: event.target.checked })}
                  />
                  {impedimentLabels[code]}
                </label>
                <label className="flex items-center gap-2 text-xs text-ink-600">
                  <input
                    type="checkbox"
                    checked={atual.liberavel}
                    disabled={!liberavelPermitido}
                    aria-label={`Admite liberacao (${impedimentLabels[code]})`}
                    onChange={(event) => muda({ liberavel: event.target.checked })}
                  />
                  {liberavelPermitido
                    ? 'Admite liberacao por alcada'
                    : 'Nao admite liberacao'}
                </label>
              </div>
              <p className="text-2xs text-ink-500">Fonte: {impedimentSources[code]}</p>
            </li>
          );
        })}
      </ul>
      <Select
        label="Renovacoes que conferem o limite"
        value={values.opcoes.renovacao_confere_limite}
        options={[
          { value: 'todas', label: 'Todas (lado seguro)' },
          {
            value: 'somente_judicial',
            label: 'So a judicial (LIM-010)',
          },
        ]}
        onChange={(event) =>
          onChange({
            ...values,
            opcoes: {
              ...values.opcoes,
              renovacao_confere_limite: event.target.value as
                'todas' | 'somente_judicial',
            },
          })
        }
      />
      <label className="flex items-center gap-2 text-sm text-ink-700">
        <input
          type="checkbox"
          checked={values.opcoes.endosso_dispensa_cadastro_vencido}
          onChange={(event) =>
            onChange({
              ...values,
              opcoes: {
                ...values.opcoes,
                endosso_dispensa_cadastro_vencido: event.target.checked,
              },
            })
          }
        />
        No endosso, nao conferir o cadastro vencido (CAD-021)
      </label>
    </div>
  );
}

// --- Fluxo ---------------------------------------------------------------------------

export function EngineFlowEditor({
  values,
  onChange,
}: {
  values: EngineFlowValues;
  onChange: (values: EngineFlowValues) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        checked={values.motor_no_envio_para_analise}
        onChange={(event) =>
          onChange({ motor_no_envio_para_analise: event.target.checked })
        }
      />
      Executar o motor de credito no envio da proposta ou da cotacao para analise (so
      grava: nao move a esteira e nao consulta a SeguraCoreCredit)
    </label>
  );
}
